import React, { useState } from "react";
import telegramService from "../utils/telegram";
import apiService from "../services/api";
import { Crown, Wallet, History, Zap, Check, Headset, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useApi } from "../hooks/useApi";
import { useToast } from "./ui/Toast";
import Button from "./ui/Button";
import SegmentedControl from "./ui/SegmentedControl";
import ShimmerSkeleton from "./ui/ShimmerSkeleton";
import ErrorState from "./ui/ErrorState";

interface Transaction {
  _id: string;
  type: string;
  amount: number;
  provider?: string;
  createdAt: string;
}

const Premium: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState(0); // 0 = plans, 1 = contact
  const [selectedPlan, setSelectedPlan] = useState("1_year");
  const [processing, setProcessing] = useState(false);

  const tgId = telegramService.getUserId();

  const {
    data: subscription,
    loading,
    error,
    refetch,
  } = useApi(() => apiService.getSubscription(tgId), [tgId], { enabled: Boolean(tgId) });

  const PLANS = [
    {
      id: "1_month",
      name: `1 ${t("premium.month")}`,
      price: 29000,
      save: null as string | null,
      features: [
        t("premium.features.unlimited"),
        t("premium.features.insights"),
        t("premium.features.noAds"),
      ],
      color: "from-blue-500 to-indigo-600",
    },
    {
      id: "6_months",
      name: `6 ${t("premium.months")}`,
      price: 129000,
      save: t("premium.savePercent"),
      features: [
        t("premium.featuresExtra.allMonthly"),
        t("premium.features.support"),
        t("premium.featuresExtra.earlyAccess"),
      ],
      color: "from-purple-500 to-pink-600",
    },
    {
      id: "1_year",
      name: `1 ${t("premium.year")}`,
      price: 199000,
      save: t("premium.bestValue"),
      features: [
        t("premium.featuresExtra.allFeatures"),
        t("premium.featuresExtra.freeMonths"),
        t("premium.featuresExtra.vipBadge"),
      ],
      color: "from-amber-400 to-orange-500",
    },
  ];

  const handlePurchase = async () => {
    const plan = PLANS.find((p) => p.id === selectedPlan);
    if (!plan || !subscription) return;

    if (subscription.balance < plan.price) {
      setActiveTab(1);
      return;
    }

    const confirmed = await telegramService.showConfirm(
      t("premium.confirmPurchase", { plan: plan.name, price: plan.price.toLocaleString() }),
    );
    if (!confirmed) return;

    setProcessing(true);
    try {
      if (tgId) {
        await apiService.purchasePlan(tgId, plan.id);
        telegramService.haptic("success");
        toast.show(t("premium.successUpgrade"), "success");
        refetch();
      }
    } catch (err: any) {
      console.error(err);
      telegramService.haptic("error");
      toast.show(
        `${t("premium.purchaseFailed")}: ${err.response?.data?.error || t("common.error")}`,
        "error",
      );
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <ShimmerSkeleton variant="stats" />;
  if (error || !subscription) return <ErrorState onRetry={refetch} />;

  const { balance, isPremium, premiumUntil } = subscription;
  const transactions: Transaction[] = subscription.transactions || [];

  const selectedPlanDetails = PLANS.find((p) => p.id === selectedPlan);
  const canAfford = selectedPlanDetails ? balance >= selectedPlanDetails.price : false;

  return (
    <div className="min-h-screen gradient-cream pb-32 relative overflow-hidden font-sans">
      {/* Dark hero header — ikkala theme'da ham to'q, premium hissiyot */}
      <div className="bg-[#1c1917] text-white rounded-b-[2rem] p-6 shadow-2xl shadow-black/20 relative z-10 pt-[calc(2.5rem+var(--safe-top))]">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-black tracking-tight">{t("premium.title")}</h1>
            <p className="text-white/50 text-xs font-medium">{t("premium.manage")}</p>
          </div>
          <div className="bg-white/5 p-2 rounded-xl backdrop-blur-md border border-white/10">
            <div className="text-[10px] text-white/50 font-bold uppercase tracking-widest mb-1">
              {t("premium.balance")}
            </div>
            <div className="flex items-center gap-2 text-emerald-400 font-black text-lg">
              <Wallet size={16} />
              {balance.toLocaleString()}
              <span className="text-[10px] self-end mb-1">UZS</span>
            </div>
          </div>
        </div>

        {/* Active Plan Card */}
        <div className="gradient-premium p-4 rounded-2xl relative overflow-hidden shadow-lg shadow-indigo-500/30">
          <div className="absolute top-0 right-0 p-3 opacity-10">
            <Crown size={100} />
          </div>
          <div className="relative z-10 flex justify-between items-center">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded text-white backdrop-blur-sm">
                  {isPremium ? t("premium.proActive") : t("premium.freePlan")}
                </span>
              </div>
              <h2 className="text-xl font-bold text-white mb-1">
                {isPremium ? t("premium.premiumMember") : t("premium.basicAccess")}
              </h2>
              <p className="text-[10px] text-indigo-100 font-medium opacity-80">
                {isPremium && premiumUntil
                  ? t("premium.expiresOn", {
                      date: new Date(premiumUntil).toLocaleDateString(),
                    })
                  : t("premium.upgradePrompt")}
              </p>
            </div>
            {!isPremium && (
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Zap className="text-yellow-300 fill-yellow-300" size={20} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="p-4">
        <SegmentedControl
          options={[t("premium.plans"), t("premium.contactSupport")]}
          selected={activeTab}
          onChange={setActiveTab}
        />
      </div>

      <div className="px-4 pb-4">
        <AnimatePresence mode="wait">
          {activeTab === 0 ? (
            <motion.div
              key="plans"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 gap-3">
                {PLANS.map((plan) => (
                  <div
                    key={plan.id}
                    onClick={() => {
                      telegramService.haptic("selection");
                      setSelectedPlan(plan.id);
                    }}
                    className={`relative p-4 rounded-2xl border-2 transition-all cursor-pointer overflow-hidden ${
                      selectedPlan === plan.id
                        ? "border-premium bg-surface shadow-elevated scale-[1.02]"
                        : "border-transparent bg-surface shadow-card hover:scale-[1.01]"
                    }`}
                  >
                    {selectedPlan === plan.id && (
                      <div
                        className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${plan.color}`}
                      />
                    )}
                    {plan.save && (
                      <div
                        className={`absolute top-0 right-0 px-3 py-1 rounded-bl-xl text-[10px] font-bold text-white bg-gradient-to-r ${plan.color}`}
                      >
                        {plan.save}
                      </div>
                    )}
                    <div className="flex justify-between items-center mb-2 pl-2">
                      <h3 className="font-bold text-content-primary text-lg">{plan.name}</h3>
                      <div className="text-right">
                        <div className="font-black text-content-primary">
                          {plan.price.toLocaleString()}
                        </div>
                        <div className="text-[10px] text-content-tertiary font-bold">UZS</div>
                      </div>
                    </div>
                    <div className="pl-2 space-y-1">
                      {plan.features.map((feat, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs text-content-secondary font-medium"
                        >
                          <Check size={12} className="text-success" /> {feat}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 mb-4">
                <Button
                  variant={canAfford ? "primary" : "premium"}
                  size="lg"
                  fullWidth
                  loading={processing}
                  onClick={canAfford ? handlePurchase : () => setActiveTab(1)}
                >
                  {canAfford ? t("premium.purchasePlan") : t("premium.contactSupport")}
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="contact"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="card p-6 mb-6 text-center">
                <div className="w-16 h-16 bg-premium-soft rounded-full flex items-center justify-center mx-auto mb-4">
                  <Headset className="text-premium" size={32} />
                </div>
                <h3 className="text-lg font-bold text-content-primary mb-2">
                  {t("premium.contactTitle")}
                </h3>
                <p className="text-sm text-content-tertiary mb-6">
                  {t("premium.contactDescription")}
                </p>

                <Button
                  variant="premium"
                  size="lg"
                  fullWidth
                  icon={MessageCircle}
                  onClick={() => telegramService.openLink("https://t.me/shohjahon1code")}
                >
                  @shohjahon1code
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-8 opacity-50 grayscale pointer-events-none select-none relative">
                <div className="absolute inset-0 z-10 flex items-center justify-center">
                  <span className="bg-content-primary text-content-inverse text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                    {t("common.comingSoon")}
                  </span>
                </div>
                <div className="py-4 bg-inset rounded-xl font-bold flex flex-col items-center justify-center gap-1">
                  <span className="text-content-tertiary font-extrabold text-lg">Click</span>
                </div>
                <div className="py-4 bg-premium-soft rounded-xl font-bold flex flex-col items-center justify-center gap-1">
                  <span className="text-premium/60 font-extrabold text-lg">Payme</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Transaction History */}
        <div className="mt-8">
          <div className="flex items-center gap-2 px-2 mb-4">
            <History size={14} className="text-content-tertiary" />
            <h3 className="section-title">{t("premium.recentActivity")}</h3>
          </div>
          <div className="card overflow-hidden p-0">
            {transactions.length === 0 ? (
              <div className="p-6 text-center text-content-tertiary text-sm">
                {t("premium.noTransactions")}
              </div>
            ) : (
              transactions.map((tx) => (
                <div
                  key={tx._id}
                  className="p-4 border-b border-line last:border-0 flex justify-between items-center"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-full ${
                        tx.type === "deposit"
                          ? "bg-success-soft text-success"
                          : "bg-danger-soft text-danger"
                      }`}
                    >
                      {tx.type === "deposit" ? <Wallet size={14} /> : <Zap size={14} />}
                    </div>
                    <div>
                      <div className="font-bold text-content-primary text-sm capitalize">
                        {tx.type === "subscription_purchase"
                          ? t("premium.planPurchase")
                          : tx.type}
                      </div>
                      <div className="text-[10px] text-content-tertiary font-medium">
                        {new Date(tx.createdAt).toLocaleDateString()}
                        {tx.provider ? ` • ${tx.provider}` : ""}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`font-black text-sm ${
                      tx.type === "deposit" ? "text-success" : "text-danger"
                    }`}
                  >
                    {tx.type === "deposit" ? "+" : ""}
                    {tx.amount.toLocaleString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Premium;
